import { IMatrixClientCreds } from "../../../MatrixClientPeg";
import { IDthContact, IDthUserSession } from "../models/httpClientModels";
import { PASSWORD_REGEX } from "../../constants";
import { parsePhoneNumberFromString, PhoneNumber } from "libphonenumber-js";
import LicenseError from "../errors/LicenseError";
import { importSPKI, JWTPayload, jwtVerify } from "jose";

//#region CONSTANTS
export const PLATFORM = "Web";
export const PLATFORM_UPPERCASE = PLATFORM.toUpperCase();
//#endregion

/**
 * Funzione che calcola l'hash della password (HMAC-SHA-256) data una chiave, restituendo la password hashata in formato esadecimale
 * @param password Password da hashare
 * @param keyText Chiave da utilizzare per il calcolo dell'HMAC
 * @returns Password hashata in formato esadecimale
 */
export async function hashPassword(password: string, keyText: string): Promise<string> {
    return await hmacSha256Hex(password, keyText);
}

/**
 * Funzione che calcola l'HMAC-SHA-256 di un messaggio data una chiave, restituendo il risultato in formato esadecimale
 * @param keyText Chiave da utilizzare per il calcolo dell'HMAC
 * @param messageText Messaggio da firmare con l'HMAC
 * @returns HMAC-SHA-256 del messaggio in formato esadecimale
*/
export async function hmacSha256Hex(keyText: string, messageText: string): Promise<string> {
    // Use Web Crypto to produce HMAC-SHA-256 with key = keyText (as utf-8), message = messageText
    const enc = new TextEncoder();
    const keyData = enc.encode(keyText);
    const msgData = enc.encode(messageText);
    const cryptoKey = await crypto.subtle.importKey(
        'raw', keyData, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
    );
    const sig = await crypto.subtle.sign('HMAC', cryptoKey, msgData);
    // convert ArrayBuffer to hex string
    const arr = new Uint8Array(sig);
    return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Funzione che calcola l'hash SHA-256 di un messaggio, restituendo il risultato in formato esadecimale
 * @param messageText Messaggio da hashare
 * @returns Hash SHA-256 del messaggio in formato esadecimale
 */
export async function sha256Hex(messageText: string): Promise<string> {
    const enc = new TextEncoder();
    const data = enc.encode(messageText);

    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    const hashArray = new Uint8Array(hashBuffer);
    return Array.from(hashArray)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

//#region LICENSING UTILS
/**
 * Enum per la gestione dell'integrity login, 
 * che indica se forzare, ignorare o tentare di applicare l'integrity login 
 * durante la validazione della licenza.
 */
export enum IntegrityLogin {
    FORCE = "force",
    NO = "no",
    IF_POSSIBLE = "if_possible"
}
/**
 * Interfaccia che estende il payload standard di un JWT 
 * con i campi specifici per la licenza
 */
export interface LicenseJWTPayload extends JWTPayload {
    app?: string;
    bundleId?: string;
    cert?: string[];
    iat?: number;
    integrityLogin?: IntegrityLogin; // IGNORE: Not used on web
    licensed: boolean;
    pinning?: boolean; // If true, enable SSL pinning (check if possible on web)
    platform?: string;
}

/**
 * Funzione che verifica un JWT di licenza utilizzando una chiave pubblica in formato PEM.
 * @param token JWT da verificare
 * @param publicKeyPEM Chiave pubblica in formato PEM utilizzata per la verifica
 * @returns Payload decodificato del JWT se valido, altrimenti lancia un LicenseError
 */
export async function verifyJwt(
    token: string,
    publicKeyPEM: string
): Promise<LicenseJWTPayload> {
    try {
        if (!publicKeyPEM.includes('BEGIN PUBLIC KEY')) {
            throw new Error('Invalid PEM format');
        }

        const pem = publicKeyPEM
            .replace(/\\n/g, '\n')
            .replace(/\r/g, '')
            .trim();

        const key = await importSPKI(pem, 'RS256');

        const result = await jwtVerify(token, key);

        return result.payload as LicenseJWTPayload;
    } catch (e) {
        console.error(e);
        throw new LicenseError(`Invalid JWT: ${e instanceof Error ? e.message : String(e)}`);
    }
}

//#endregion 

/**
 * Funzione che valida una password
 * @param password 
 * @returns 
 */
export function validatePassword(password: string): boolean {
    return PASSWORD_REGEX.test(password);
}

/**
 * Matrix User Session to Matrix Creds
 * @param homeserverUrl 
 * @param matrixSession 
 * @returns 
 */
export function convertIDthUserSessionToMatrixClientCreds(
    homeserverUrl: string,
    dthUserSession: IDthUserSession,
    identityServerUrl?: string): IMatrixClientCreds {
    const session = dthUserSession.session;
    const userInfo = dthUserSession.user;
    return {
        homeserverUrl: homeserverUrl,
        identityServerUrl: identityServerUrl,
        apiUserId: userInfo.id,
        userId: session.matrixUserId,
        deviceId: session.deviceId,
        apiAccessToken: session.accessToken,
        apiExpirationToken: session.expirationDate,
        apiRefreshToken: session.refreshToken,
        accessToken: session.matrixAccessToken,
        refreshToken: session.matrixRefreshToken,
        pickleKey: undefined,
        guest: false
    };
}

/**
 * Ottiene il contatto associato a un display name, confrontando sia "Nome Cognome" che "Cognome Nome"
 * @param contacts Lista dei contatti dell'utente
 * @param displayName Display name da cercare
 * @returns Contatto trovato o undefined se non trovato
 */
export function findContactByDisplayName(
    contacts: IDthContact[],
    displayName: string
): IDthContact | undefined {
    const normalizedDisplay = displayName.trim().toLowerCase();

    return contacts.find(c => {
        const nameSurname = `${c.firstName} ${c.lastName}`.toLowerCase();
        const surnameName = `${c.lastName} ${c.firstName}`.toLowerCase();

        return (
            normalizedDisplay === nameSurname ||
            normalizedDisplay === surnameName
        );
    });
}

/**
 * Converte un Matrix User ID nel participant ID leggibile, estraendo la parte del participant ID prima del ":" e rimuovendo la "@" iniziale
 * @param matrixUserId Matrix User ID (es: "@participantId:domain.com")
 * @returns participant ID (es: "participantId")
 */
export function matrixIdToParticipantId(matrixUserId: string): string {
    const match = matrixUserId.match(/^@([^:]+):/);
    // if (!match) {
    //     console.warn(`Invalid Matrix User ID format: ${matrixUserId}`);
    // }
    return match ? match[1] : matrixUserId;
}

/**
 * Converte un participant ID in un Matrix User ID completo
 * @param participantId Participant ID (es: "participantId")
 * @param domain Dominio Matrix (es: "domain.com")
 * @returns Matrix User ID (es: "@participantId:domain.com")
 */
export function participantIdToMatrixId(participantId: string, domain: string): string {
    if (!participantId.startsWith("@") && domain) {
        return `@${participantId}:${domain}`;
    }
    // Se già in formato Matrix ID, restituiscilo così com'è
    // console.warn(`Unexpected Matrix participant ID format or domain missing: participantId="${participantId}" domain="${domain}"`);
    return participantId;
}

/**
 * Estrae il dominio da un Matrix User ID
 * @param matrixId Matrix User ID (es: "@participantId:domain.com")
 * @returns Dominio estratto (es: "domain.com") o stringa vuota se il formato è invalido
 */
export function getDomainFromMatrixId(matrixId: string): string {
    const match = matrixId.match(/^@[^:]+:(.+)$/);
    // if (!match) {
    //     console.warn(`Invalid Matrix User ID format: ${matrixId}`);
    // }
    return match ? match[1] : "";
}

/**
 * Converte un URL con un protocollo in un URL WSS, 
 * sostituendo il protocollo con "wss://"
 * @param url URL HTTP/HTTPS da convertire
 * @returns URL WSS convertito
 */
export function toWssUrl(url: string): string {
    return url.replace(/^[a-zA-Z]+:\/\//, "wss://");
}

//#region Regex di validazione per phone
/**
 * Parsing e validazione avanzata di un numero di telefono internazionale.
 * @param phone Numero da validare (es: "+393331234567" o "3331234567")
 * @param defaultCountry Codice paese di default (es: "IT", "US") se il numero non ha prefisso
 * @returns Oggetto PhoneNumber se valido, null altrimenti
 */
export function parseAndValidatePhone(phone: string): PhoneNumber | null {
    try {
        // Normalizza e rimuove spazi/trattini
        const normalized = phone.replace(/[\s\-()]/g, "");
        // Deve iniziare con + (prefisso internazionale obbligatorio)
        if (!normalized.startsWith("+")) {
            return null;
        }
        const phoneNumber = parsePhoneNumberFromString(normalized);
        if (phoneNumber && phoneNumber.isValid()) {
            return phoneNumber;
        }
        return null;
    } catch {
        return null;
    }
}
//#endregion